import cn from 'clsx'

import React from 'react'
import { useDrop } from 'react-dnd'
import * as ItemTypes from '@disco/constants/ItemTypes'
import styles from './index.module.css'

type Props = {
  accept: typeof ItemTypes[keyof typeof ItemTypes]
  children: React.ReactNode
  className?: string
  onDrop: (id: number) => void
}

/**
 * A drag and drop zone component that accepts draggable items and handles drop events
 * @param accept - The type of items this drop zone accepts (from ItemTypes constants)
 * @param children - The content to render inside the drop zone
 * @param className - Additional CSS classes to apply to the drop zone
 * @param onDrop - Callback function called when an item is dropped, receives the item's ID
 * @returns A div element that serves as a drop zone with drag-over styling
 */
const DropZone = ({
  accept,
  children,
  className,
  onDrop,
}: Props): React.ReactElement<'div'> => {
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept,
      collect: monitor => ({
        isOver: monitor.isOver(),
      }),
      drop: (item: { id: number }) => {
        onDrop(item.id)
      },
    }),
    [onDrop],
  )
  return (
    <div
      className={cn(className, styles.dropZone, {
        [styles.dropZoneOver]: isOver,
      })}
      ref={drop}
    >
      {children}
    </div>
  )
}

export default DropZone
